"use client"

import { clx } from "@medusajs/ui"

import { getProductPrice } from "@lib/util/get-product-price"
import { getDiscountedPrice } from "@lib/util/promotions"
import { usePromotions } from "@lib/context/promotions-context"
import { HttpTypes } from "@medusajs/types"

export default function ProductPrice({
  product,
  variant,
}: {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
}) {
  const promotions = usePromotions()
  const { cheapestPrice, variantPrice } = getProductPrice({
    product,
    variantId: variant?.id,
  })

  const selectedPrice = variant ? variantPrice : cheapestPrice

  if (!selectedPrice) {
    return <div className="block w-32 h-9 bg-gray-100 animate-pulse" />
  }

  // Descuento por promociones automáticas de Medusa (espejo del feed de
  // Google). El % mostrado se deriva del precio real, nunca del tag.
  const original = selectedPrice.calculated_price_number
  const discounted = getDiscountedPrice(
    product,
    promotions,
    original,
    selectedPrice.currency_code.toUpperCase()
  )
  const hasPromo = discounted !== null && discounted < original
  const pct = hasPromo ? Math.round((1 - discounted! / original) * 100) : 0

  return (
    <div className="flex flex-col gap-1 py-4">
      {hasPromo ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={clx("text-4xl font-bold tracking-tight text-orange-600")}
            >
              <span
                data-testid="product-price"
                data-value={discounted!.toFixed(2)}
              >
                {discounted!.toFixed(2)}€
              </span>
            </span>
            <span className="text-sm font-bold text-orange-500 uppercase tracking-widest bg-orange-50 px-2 py-1 rounded-md border border-orange-100 italic">Dto. -{pct}%</span>
          </div>

          <div className="flex items-center gap-2 text-gray-400">
            <span className="text-xs font-bold uppercase tracking-widest">Normal: </span>
            <span
              className="text-lg font-bold line-through decoration-2"
              data-testid="original-product-price"
              data-value={original.toFixed(2)}
            >
              {original.toFixed(2)} €
            </span>
            <span className="text-[10px] font-bold uppercase tracking-tighter bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200 ml-1">Iva incluido</span>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className={clx("text-4xl font-bold tracking-tight text-gray-900", {
              "text-orange-600": selectedPrice.price_type === "sale",
            })}
          >
            <span
              data-testid="product-price"
              data-value={original.toFixed(2)}
            >
              {original.toFixed(2)}€
            </span>
          </span>
          <span className="text-sm font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-2 py-1 rounded-md border border-gray-100">Iva incluido</span>
        </div>
      )}
    </div>
  )
}
